import * as React from "react";

import { Button, type ButtonProps } from "~/components/ui/button";
import { cn } from "~/lib/utils";

import "./glass-option-button.css";

type GlassOptionButtonProps = Omit<
  ButtonProps,
  "active" | "aria-pressed" | "size" | "variant"
> & {
  /** Controlled visual and accessible pressed state. */
  selected?: boolean;
};

interface GlassOptionButtonStyle extends React.CSSProperties {
  readonly "--glass-option-button-filter": string;
}

function GlassOptionFilter({ id }: { readonly id: string }) {
  return (
    <svg
      aria-hidden="true"
      className="glass-option-button-filter-defs"
      data-slot="glass-option-button-filter"
      focusable="false"
    >
      <defs>
        <filter
          id={id}
          colorInterpolationFilters="sRGB"
          height="160%"
          width="116%"
          x="-8%"
          y="-30%"
        >
          <feMorphology
            in="SourceAlpha"
            operator="erode"
            radius="1.4427"
            result="glass-fresnel-interior"
          />
          <feComposite
            in="SourceAlpha"
            in2="glass-fresnel-interior"
            operator="out"
            result="glass-fresnel-mask"
          />
          <feFlood
            floodColor="#fff"
            floodOpacity="0.26"
            result="glass-fresnel-color"
          />
          <feComposite
            in="glass-fresnel-color"
            in2="glass-fresnel-mask"
            operator="in"
            result="glass-fresnel"
          />
          <feGaussianBlur
            in="SourceAlpha"
            result="glass-height"
            stdDeviation="1"
          />
          <feDiffuseLighting
            diffuseConstant="0.24"
            in="glass-height"
            lightingColor="#fff"
            result="glass-diffuse-light"
            surfaceScale="7.2137"
          >
            <feDistantLight azimuth="225" elevation="45" />
          </feDiffuseLighting>
          <feComposite
            in="glass-diffuse-light"
            in2="glass-fresnel-mask"
            operator="in"
            result="glass-diffuse"
          />
          <feBlend
            in="glass-fresnel"
            in2="glass-diffuse"
            mode="screen"
            result="glass-lit-fresnel"
          />
          <feSpecularLighting
            in="glass-height"
            lightingColor="#fff"
            result="glass-light"
            specularConstant="0.8"
            specularExponent="16"
            surfaceScale="7.2137"
          >
            <feDistantLight azimuth="225" elevation="45" />
          </feSpecularLighting>
          <feComposite
            in="glass-light"
            in2="SourceAlpha"
            operator="in"
            result="glass-clipped"
          />
          <feMorphology
            in="SourceAlpha"
            operator="erode"
            radius="2.25"
            result="glass-specular-interior"
          />
          <feComposite
            in="glass-clipped"
            in2="glass-specular-interior"
            operator="out"
            result="glass-specular"
          />
          <feMerge>
            <feMergeNode in="glass-lit-fresnel" />
            <feMergeNode in="glass-specular" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  );
}

const GlassOptionButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  GlassOptionButtonProps
>(function GlassOptionButton(
  { children, className, selected = false, style, type = "button", ...props },
  ref,
) {
  const filterId = `glass-option-button-${React.useId().replace(/:/g, "")}`;
  const resolvedStyle: GlassOptionButtonStyle = {
    ...style,
    "--glass-option-button-filter": `url("#${filterId}")`,
  };

  return (
    <>
      <GlassOptionFilter id={filterId} />
      <Button
        {...props}
        ref={ref}
        aria-pressed={selected}
        className={cn("glass-option-button", className)}
        data-selected={selected}
        style={resolvedStyle}
        type={type}
        variant="ghost"
      >
        {children}
      </Button>
    </>
  );
});

GlassOptionButton.displayName = "GlassOptionButton";

export { GlassOptionButton };
export type { GlassOptionButtonProps };
